/**
 * downloadTemplate — save one of the static EIA template files (public/templates).
 *
 * Why not a plain <a href download>: when a template file is missing, the
 * server's SPA fallback answers with index.html (status 200), and the browser
 * saves that page under the .xlsx name — a "template" Excel refuses to open,
 * with nothing on screen to say why. So the file is fetched first and checked:
 * every .xlsx is a ZIP archive and starts with the bytes "PK". Only a real
 * spreadsheet is saved; anything else becomes an error the UI can show.
 *
 * Static same-origin asset, not server data — so a direct fetch here, not
 * RTK Query (.claude/rules/state.md covers API data).
 */

export const TEMPLATE_UNAVAILABLE_MESSAGE =
  'This template is not available right now. Please try again later or contact support.';

/** .xlsx (and every ZIP) starts with "PK\x03\x04". */
function isZip(bytes) {
  return (
    bytes.length >= 4 &&
    bytes[0] === 0x50 &&
    bytes[1] === 0x4b &&
    bytes[2] === 0x03 &&
    bytes[3] === 0x04
  );
}

/**
 * A blob's bytes. `Blob.arrayBuffer()` is missing in older Safari (< 14) — and
 * in the test DOM — so FileReader is the fallback.
 */
function readBytes(blob) {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

/**
 * @param {{href: string, filename: string}} template
 * @returns {Promise<void>} rejects with TEMPLATE_UNAVAILABLE_MESSAGE when the
 *   file is missing or is not a spreadsheet.
 */
export async function downloadTemplate({ href, filename }) {
  let blob;
  try {
    const response = await fetch(href, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    blob = await response.blob();
    const head = new Uint8Array(await readBytes(blob.slice(0, 4)));
    if (!isZip(head)) throw new Error('not an .xlsx file');
  } catch (cause) {
    // Name the missing file for whoever reads the console; the user gets the
    // plain message.
    console.error(`[templates] ${href} could not be downloaded:`, cause);
    throw new Error(TEMPLATE_UNAVAILABLE_MESSAGE);
  }

  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Revoke on the next tick: some browsers start the save after click() returns.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}
